// Phase 104: Privacy mode.
//
// When privacy_mode = true in db.settings, monetary amounts across the
// app render as `••••••` by default. Tapping any amount reveals it
// for 5 seconds, then auto-hides. Off by default — users who don't
// enable it see no change.
//
// Components can either:
//   1. Use `<Money amount={n} currency={c} />` for tap-to-reveal.
//   2. Use `usePrivacy()` hook to gate their own rendering if they
//      need a custom layout (e.g. progress bars in Goals).
//
// PrivacyProvider sits at App root after AuthProvider so the
// `data.getSettings()` call has a seeded IDB to read from.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { data, writes } from './dataService.js';
import { formatMoney } from './format.js';

const PrivacyContext = createContext({ enabled: false, toggle: () => {}, refetch: () => {} });

// Phase 151 (M-78): exported for tests so they can wrap a stub
// provider without spinning up the full PrivacyProvider (which calls
// data.getSettings() on mount, hitting Dexie in jsdom).
export { PrivacyContext };

export function usePrivacy() {
  return useContext(PrivacyContext);
}

export function PrivacyProvider({ children }) {
  const [enabled, setEnabled] = useState(false);

  // Phase 104: read db.settings.privacy_mode on mount.
  // NOTE: settingsRepo.getAll() returns { [key]: value } (an object),
  // NOT an array of rows. The original .find() pattern silently always
  // returned undefined → enabled stayed false forever, even though
  // IDB had privacy_mode=true stored. Toggle visually "worked" because
  // the local toggle() flipped React state, but every mount/route
  // change re-read IDB via this effect and reset back to false.
  const refetch = useCallback(async () => {
    try {
      const all = await data.getSettings();
      setEnabled(all && all.privacy_mode === true);
    } catch (e) {
      // Default to off on read failure — never accidentally hide amounts.
      setEnabled(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  // Phase 107.5: REMOVED the `finance:privacy-changed` window listener.
  // Reason: it triggered a self-race condition. The persist effect
  // dispatches the event after writing to IDB; the listener then calls
  // refetch(), which races with the just-completed write — sometimes
  // reading a stale (or empty) IDB value, which then setEnabled(false)
  // back to default, undoing the user's toggle. CDP verified: toggle
  // fired → enabled=true (correct) → event dispatched → refetch ran →
  // enabled=false (race) → aria-checked stayed 'false'.
  //
  // The listener was originally for cross-component updates, but the
  // Money wrapper re-reads via React context (which IS in sync), so
  // the window event broadcast isn't needed for same-tab toggles.
  // If we ever need cross-tab sync, do it via the BroadcastChannel
  // API with a separate flag — not the same-tab dispatch loop.

  const toggle = useCallback(() => {
    setEnabled(prev => !prev);
  }, []);

  // Persist to IDB on every toggle. Use a ref guard so the initial
  // mount (where refetch already loaded the value) doesn't re-write.
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      return; // Skip the initial mount — refetch() already handled it.
    }
    writes.setSetting('privacy_mode', enabled).catch(() => {});
  }, [enabled]);

  return (
    <PrivacyContext.Provider value={{ enabled, toggle, refetch }}>
      {children}
    </PrivacyContext.Provider>
  );
}

// ── <Money> ──────────────────────────────────────────────────────────
// Drop-in replacement for raw {formatMoney(amount, currency)} that
// masks the value when privacy mode is on. Tap to reveal for 5s.
const REVEAL_MS = 5000;

export function Money({ amount, currency = '$', className = '', as = 'span' }) {
  const { enabled } = usePrivacy();
  const [revealed, setRevealed] = useState(false);
  const timerRef = useRef(null);

  // Reset reveal when amount changes — prevents stale reveal across
  // different rows in a list.
  useEffect(() => {
    setRevealed(false);
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, [amount]);

  // Reset reveal when privacy toggles OFF — never leave values masked
  // when the user has explicitly turned the feature off.
  useEffect(() => {
    if (!enabled) {
      setRevealed(false);
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    }
  }, [enabled]);

  const showValue = !enabled || revealed;

  const handleClick = (e) => {
    if (!enabled) return;
    e.stopPropagation();
    setRevealed(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setRevealed(false), REVEAL_MS);
  };

  // Phase 104 mask: produce a same-width mask of bullets. Use roughly
  // one bullet per digit so layout doesn't jump on reveal.
  const masked = (() => {
    const digits = Math.abs(Number(amount) || 0).toFixed(2).replace(/[^0-9]/g, '').length;
    return '•'.repeat(Math.max(4, digits));
  })();

  const Tag = as;
  const displayText = showValue ? formatMoney(amount, currency) : masked;

  if (!enabled) {
    // Fast path: no privacy mode → plain text, no button wrapper.
    return <Tag className={className}>{displayText}</Tag>;
  }

  return (
    <Tag
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e); } }}
      aria-label={revealed ? `金額 ${displayText},點擊隱藏` : '金額已隱藏,點擊顯示'}
      title={revealed ? '點擊隱藏' : '點擊顯示金額'}
      className={`${className} cursor-pointer select-none active:opacity-60`}
      data-testid="money-amount"
      data-revealed={revealed ? 'true' : 'false'}
    >
      {displayText}
    </Tag>
  );
}